import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from "./index.module.scss";
import {useSelector, useDispatch} from "react-redux";
import {phoneNumberFormat} from "../../utils";
import {File, Order, User} from "../../services";
import { useUserData } from '../../hooks/useUserData';
import {useCompanyData} from "../../hooks/useCompanyData";
import {useNavigate} from "react-router-dom";
import ProfileCompanyCount from "./components/OrdersCount/ProfileCompanyCount";
import ProfileDocuments from "./components/UploadDocuments/ProfileDocuments";
import ProfilePassword from "./components/ProfilePassword/ProfilePassword";
import ChangeAvatar from "./components/ChangeAvatar/ChangeAvatar";
import ProfileEdit from "./components/ProfileEdit/ProfileEdit";
import ProfileNotifications from "./components/ProfileNotifications/ProfileNotifications";
import LegalInfo from "./components/LegalInfo/LegalInfo";
import { motion } from "framer-motion";
import moment from 'moment';
import {useAnimationConfig} from "../../hooks/useAnimationConfig";
import { toast } from 'react-toastify';

const ProfilePageComponentTwo = ({ changeDisplay, changeModeration}) => {
    const { t } = useTranslation();
    const { user, refreshUserData } = useUserData();
    const {companyData, refreshCompanyData } = useCompanyData();
    const navigate = useNavigate();
    const { routeVariants} = useAnimationConfig()
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [ordersCount, setOrdersCount] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const isExpeditor = user.type === "Expeditor";
    const companyId = user.companyId === null? "" : user.companyId; 

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const handleSaveEdit = async () => {
        setIsEditing(false);
        
        await refreshUserData();

        if (user?.companyId) {
            try {
                await refreshCompanyData(user.companyId);
            } catch (error) {
                console.log(error);
            }
        }
    };

    useEffect(() => {
            Order.getCompletedOrdersCount(companyId).then((response) => {
                if(response.status == 200) {
                    setOrdersCount(response.data)
                }
            }).catch(error => {
                console.log(error);
            })
    }, [user]);

    const onOpenModal = (type) => {
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
    };

    const openDocument = (type) => {
        File.get(type)
            .then(response => {
                const file = new Blob([response.data], {
                    type: response.data.type,
                });

                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);

            })
            .catch(error => {
                console.log(error);
                toast.error(t('profileTwo.toast.genericError'));
            });
    }

    const changePassword = () =>{
        navigate('/change-password')
    }

  const formattedDate = moment(user.createdAt).format('DD.MM.YYYY');

    const parseCargoTypes = (cargoTypeString) => {
        if (!cargoTypeString) return '-';
        try {
            const cargoTypes = JSON.parse(cargoTypeString);
            return cargoTypes.map(item => item.name).join(', ');
        } catch (e) {
            return '-';
        }
    };

    const parseRegions = (regionsString) => {
        if (!regionsString) return '-';
        try {
            const regions = JSON.parse(regionsString);
            return regions.map(item => item.name).join(', ');
        } catch (e) {
            return '-';
        }
    };

    return(
        <motion.div
          variants={routeVariants}
          initial="initial"
          animate="final"
          className={styles['profile__page']}
        >
            <div className={styles['profile__page-title']}>
              {t('profileTwo.pageTitle.base')}
              { isExpeditor ? t('profileTwo.pageTitle.expeditorSuffix') : t('profileTwo.pageTitle.companySuffix')}
            </div>
            <div className={styles['profile__company']}>
                <div className={styles['profile__company-title']}>{companyData?.name}</div>
                <hr className={styles['profile__company-hr']} />

                {isEditing ? (
                    <ProfileEdit
                        user={user}
                        onCancel={handleCancelEdit}
                        onSave={handleSaveEdit}
                        companyData={companyData}
                    />
                ) : (
                  <div className={styles["profile__company-header"]} style={{
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      position: "relative",
                  }}>
                      <div style={{ display: "flex", alignItems: "center", flex: "1" }}>
                          <ChangeAvatar
                            onOpenModal={onOpenModal}
                            user={user}
                          />
                          <div className={styles["profile__company-content"]}>
                              {user.role === "Employee" || user.role === "Admin" ? (
                                <>
                                    <div className={styles["profile__company-name"]}>{user.name}</div>
                                    <div
                                      className={styles["profile__company-name"]}>{user.surname} {user.patronymic}</div>
                                </>
                              ) : (
                                <>
                                    <div className={styles["profile__company-name"]}>{user.surname}</div>
                                    <div
                                      className={styles["profile__company-name"]}>{user.name} {user.patronymic}</div>
                                </>
                              )}
                          </div>
                      </div>

                      <button
                        title={t("profileTwo.buttons.editProfileTitle")}
                        className={styles["profile__upload-button"]}
                        onClick={handleEditClick}
                        style={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                        }}
                      >
                          {t("profileTwo.buttons.edit")}
                      </button>
                  </div>
                )}

                {!isEditing && (
                    <>
                        <ProfileCompanyCount totalCount={ordersCount?.totalCount} totalSum={ordersCount?.totalSum} />
                        <div className={styles["profile__contact"]}>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.contactInfo.binLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{user.bin || '-'}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.contactInfo.phoneLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{user.phoneNumber ? phoneNumberFormat(user.phoneNumber) : '-'}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.contactInfo.emailLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{user.email || '-'}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.contactInfo.ndsLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>
                                {user.isNDS ? t('profileTwo.contactInfo.ndsEnabled') : t('profileTwo.contactInfo.ndsDisabled')}
                            </div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.contactInfo.addressLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{user.address || '-'}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.company.foundedYearLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{companyData?.foundedYear || '-'}</div>
                            {!isExpeditor && (
                                <>
                                <div className={styles["profile__contact-title"]}>
                                    {t("profileTwo.company.avgVolumeLabel")}
                                </div>
                                <div className={styles["profile__contact-subtitle"]}>{companyData?.avgVolume || "-"}</div>
                                <div className={styles["profile__contact-title"]}>
                                    {t("profileTwo.company.cargoTypeLabel")}
                                </div>

                                <div className={styles["profile__contact-subtitle"]}>
                                    {parseCargoTypes(companyData?.cargoType)}
                                </div>
                                </>
                            )}
                            <div className={styles['profile__contact-title']}>{t('profileTwo.company.regionsLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{parseRegions(companyData?.regions)}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.company.contactLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{companyData?.contact || '-'}</div>
                            <div className={styles['profile__contact-title']}>{t('profileTwo.company.descriptionLabel')}</div>
                            <div className={styles['profile__contact-subtitle']}>{companyData?.description || '-'}</div>
                            <div className={styles['profile__contact-title']}> {t('profileTwo.contactInfo.registeredLabel')} </div>
                            <div className={styles['profile__contact-subtitle']}>
                              {formattedDate}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <ProfileNotifications />
            <ProfileDocuments user={user} onOpenModal={onOpenModal} openDocument={openDocument}/>
            <LegalInfo />
            <ProfilePassword user={user} onOpenModal={onOpenModal} closeModal= {closeModal} isModalOpen={isModalOpen} changePassword={changePassword} changeDisplay={changeDisplay} changeModeration={changeModeration}/>
        </motion.div>
    )
}

export default ProfilePageComponentTwo;
